import { useState, useCallback, useEffect } from 'react';
import { supabase } from '~/services/supabase';
import { trackEvent, identifyUser } from '~/services/posthog';
import type { IFormSubmission, IUTMParams } from '~/types';

interface UseFormSubmissionOptions {
  formId: string;
  formVersion: number;
  utmParams: IUTMParams;
}

interface UseFormSubmissionResult {
  submissionId: string | null;
  saveSubmission: (formData: Record<string, unknown>, currentStep?: number) => Promise<void>;
  markComplete: () => Promise<void>;
  loading: boolean;
  error: string | null;
}

export const useFormSubmission = ({
  formId,
  formVersion,
  utmParams,
}: UseFormSubmissionOptions): UseFormSubmissionResult => {
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load existing submission from localStorage if available
  useEffect(() => {
    const stored = localStorage.getItem(`submission_${formId}`);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setSubmissionId(parsed.id);
      } catch (err) {
        console.error('Failed to parse stored submission:', err);
      }
    }
  }, [formId]);

  // Save on beforeunload
  useEffect(() => {
    const handleBeforeUnload = () => {
      // This will be handled by the component calling saveSubmission
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const saveSubmission = useCallback(
    async (formData: Record<string, unknown>, currentStep?: number) => {
      setLoading(true);
      setError(null);

      try {
        const email = formData.email as string;
        if (!email) {
          // No email yet — skip save silently (auto-save will retry once user fills it in)
          return;
        }

        const submission: Partial<IFormSubmission> = {
          form_id: formId,
          form_version: formVersion,
          form_data: formData,
          email,
          current_step: currentStep,
          is_complete: false,
          ...utmParams,
        };

        if (submissionId) {
          // Update existing submission
          const updateData: Record<string, unknown> = {
            form_data: formData,
            last_activity_at: new Date().toISOString(),
          };
          if (currentStep !== undefined) {
            updateData.current_step = currentStep;
          }

          const { error: updateError } = await supabase
            .from('form_submissions')
            .update(updateData)
            .eq('id', submissionId);

          if (updateError) throw updateError;

          trackEvent('form_updated', { form_id: formId, email, current_step: currentStep });
        } else {
          // Check for an existing incomplete submission by email (e.g. user
          // returned in a new browser session). This lets us resume instead
          // of creating a duplicate row.
          const { data: existing } = await supabase
            .from('form_submissions')
            .select('id')
            .eq('form_id', formId)
            .eq('email', email)
            .eq('is_complete', false)
            .is('deleted_at', null)
            .maybeSingle();

          if (existing) {
            // Resume the existing incomplete submission
            setSubmissionId(existing.id);
            localStorage.setItem(
              `submission_${formId}`,
              JSON.stringify({ id: existing.id, current_step: currentStep }),
            );

            const updateData: Record<string, unknown> = {
              form_data: formData,
              last_activity_at: new Date().toISOString(),
            };
            if (currentStep !== undefined) {
              updateData.current_step = currentStep;
            }

            const { error: updateError } = await supabase
              .from('form_submissions')
              .update(updateData)
              .eq('id', existing.id);

            if (updateError) throw updateError;

            identifyUser(email);
            trackEvent('form_resumed', { form_id: formId, email, current_step: currentStep });
          } else {
            // Create new submission with client-generated UUID
            const id = crypto.randomUUID();
            const { error: insertError } = await supabase.from('form_submissions').insert({ ...submission, id });

            if (insertError) {
              // Handle race condition: another tab may have inserted between
              // our SELECT and INSERT, violating the partial unique index.
              const pgError = insertError as { code?: string };
              if (pgError.code === '23505') {
                const { data: raceExisting } = await supabase
                  .from('form_submissions')
                  .select('id')
                  .eq('form_id', formId)
                  .eq('email', email)
                  .eq('is_complete', false)
                  .is('deleted_at', null)
                  .maybeSingle();

                if (raceExisting) {
                  setSubmissionId(raceExisting.id);
                  localStorage.setItem(
                    `submission_${formId}`,
                    JSON.stringify({ id: raceExisting.id, current_step: currentStep }),
                  );
                  identifyUser(email);
                  trackEvent('form_resumed', { form_id: formId, email, current_step: currentStep });
                  return;
                }
              }
              throw insertError;
            }

            setSubmissionId(id);
            localStorage.setItem(`submission_${formId}`, JSON.stringify({ id, current_step: currentStep }));

            identifyUser(email);
            trackEvent('form_started', { form_id: formId, email, current_step: currentStep });
          }
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to save submission';
        setError(errorMessage);
        console.error('Error saving submission:', err);
      } finally {
        setLoading(false);
      }
    },
    [formId, formVersion, submissionId, utmParams],
  );

  const markComplete = useCallback(async () => {
    if (!submissionId) return;

    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('form_submissions')
        .update({
          is_complete: true,
          last_activity_at: new Date().toISOString(),
        })
        .eq('id', submissionId);

      if (updateError) throw updateError;

      trackEvent('form_completed', { form_id: formId });

      // Fire-and-forget HubSpot sync — cron will retry if this fails
      supabase.functions
        .invoke('hubspot-sync', {
          body: { action: 'syncSubmission', submissionId, syncType: 'complete' },
        })
        .catch(console.error);

      // Clear localStorage after completion
      localStorage.removeItem(`submission_${formId}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to mark complete';
      setError(errorMessage);
      console.error('Error marking submission complete:', err);
    } finally {
      setLoading(false);
    }
  }, [submissionId, formId]);

  return { submissionId, saveSubmission, markComplete, loading, error };
};
